import React, { useState, useEffect } from "react";
import { X, Crown, AlertCircle, CheckCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { grantPremiumAccessAction } from "../../../store/slices/premiumAccess/premiumAccessSliceActions";
import { clearPremiumAccessMessages } from "../../../store/slices/premiumAccess/premiumAccessSlice";
import { colors } from "../../../constants/colors";

const PremiumAccessModal = ({ isOpen, onClose, userId = "" }) => {
  const dispatch = useAppDispatch();
  const { isGrantingPremium, grantPremiumSuccess, error } = useAppSelector(
    (state) => state.premiumAccess
  );

  const [formData, setFormData] = useState({
    userId: userId,
    period: "monthly",
    durationMonths: "",
  });

  // Update form when userId prop changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      userId: userId || "",
    }));
  }, [userId]);

  // Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearPremiumAccessMessages());
    };
  }, [dispatch]);

  // Auto-close modal on success after 2 seconds
  useEffect(() => {
    if (grantPremiumSuccess) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [grantPremiumSuccess, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.userId.trim()) {
      return;
    }

    const payload = {
      userId: formData.userId.trim(),
      period: formData.period,
    };

    // Add duration months if specified
    if (formData.durationMonths && parseInt(formData.durationMonths) > 0) {
      payload.durationMonths = parseInt(formData.durationMonths);
    }

    dispatch(grantPremiumAccessAction(payload));
  };

  const handleClose = () => {
    if (!isGrantingPremium) {
      dispatch(clearPremiumAccessMessages());
      onClose();
    }
  };

  if (!isOpen) return null;

  // Modal styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const modalStyle = {
    backgroundColor: colors.white,
    borderRadius: "16px",
    padding: "2rem",
    width: "100%",
    maxWidth: "500px",
    margin: "1rem",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1.5rem",
  };

  const titleStyle = {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: colors.darkGreen,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const closeButtonStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0.5rem",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.darkGray,
    transition: "all 0.2s ease",
  };

  const formStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  };

  const labelStyle = {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: colors.darkBlue,
    marginBottom: "0.5rem",
  };

  const inputStyle = {
    padding: "0.75rem",
    border: `2px solid ${colors.lightGray}`,
    borderRadius: "8px",
    fontSize: "1rem",
    transition: "border-color 0.2s ease",
    width: "100%",
  };

  const selectStyle = {
    ...inputStyle,
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23666' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 0.7rem center",
    backgroundSize: "0.65rem auto",
    cursor: "pointer",
  };

  const buttonStyle = {
    padding: "0.875rem 1.5rem",
    backgroundColor: isGrantingPremium ? colors.lightGray : colors.darkGreen,
    color: colors.white,
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: isGrantingPremium ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  };

  const messageStyle = (type) => ({
    padding: "1rem",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.9rem",
    fontWeight: "500",
    backgroundColor: type === "success" ? "#d4edda" : "#f8d7da",
    color: type === "success" ? "#155724" : "#721c24",
    border: `1px solid ${type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
    marginBottom: "1rem",
  });

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            <Crown size={24} />
            Grant Premium Access
          </h2>
          <button
            style={closeButtonStyle}
            onClick={handleClose}
            disabled={isGrantingPremium}
          >
            <X size={20} />
          </button>
        </div>

        {grantPremiumSuccess && (
          <div style={messageStyle("success")}>
            <CheckCircle size={16} />
            Premium access granted successfully! Closing modal...
          </div>
        )}

        {error && (
          <div style={messageStyle("error")}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form style={formStyle} onSubmit={handleSubmit}>
          <div>
            <label style={labelStyle}>User ID</label>
            <input
              type="text"
              name="userId"
              value={formData.userId}
              onChange={handleInputChange}
              style={inputStyle}
              placeholder="Enter user ID"
              required
              disabled={isGrantingPremium}
            />
          </div>

          <div>
            <label style={labelStyle}>Subscription Period</label>
            <select
              name="period"
              value={formData.period}
              onChange={handleInputChange}
              style={selectStyle}
              required
              disabled={isGrantingPremium}
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Duration (Months) - Optional</label>
            <input
              type="number"
              name="durationMonths"
              value={formData.durationMonths}
              onChange={handleInputChange}
              style={inputStyle}
              placeholder="Leave empty for default duration"
              min="1"
              max="120"
              disabled={isGrantingPremium}
            />
            <div
              style={{
                fontSize: "0.8rem",
                color: colors.darkGray,
                marginTop: "0.25rem",
              }}
            >
              Default: 1 month for monthly, 12 months for yearly
            </div>
          </div>

          <button
            type="submit"
            style={buttonStyle}
            disabled={isGrantingPremium || !formData.userId.trim()}
          >
            {isGrantingPremium ? (
              <>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff40",
                    borderTop: "2px solid #ffffff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Granting Premium...
              </>
            ) : (
              <>
                <Crown size={16} />
                Grant Premium Access
              </>
            )}
          </button>
        </form>

        <style jsx>{`
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PremiumAccessModal;
